import React, { useState, useEffect } from 'react';
import { BottomSheet } from './BottomSheet';
import './PWAInstallPrompt.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallPrompt: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent) ||
        window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if already installed
  useEffect(() => {
    const checkInstalled = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
      setIsInstalled(isStandalone);
    };

    checkInstalled();
  }, []);

  // Listen for beforeinstallprompt event and show prompt on mobile
  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const evt = event as BeforeInstallPromptEvent;
      setDeferredPrompt(evt);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show prompt immediately on mobile devices (after 1 second)
    if (isMobile && !isInstalled) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [isMobile, isInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      setIsOpen(false);
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Installation failed:', error);
    }
  };

  const handleLater = () => {
    setIsOpen(false);
  };

  // Show if mobile and not installed
  if (!isMobile || isInstalled) {
    return null;
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={handleLater} showCloseButton={true}>
      <div className="pwa-install-prompt">
        <div className="pwa-install-header">
          <div className="pwa-install-icon">📲</div>
          <h3 className="pwa-install-title">Add to Home Screen</h3>
        </div>

        <p className="pwa-install-description">
          Install CoworkAtLevel3 for quick access. Get the app experience right from your home screen.
        </p>

        <div className="pwa-install-benefits">
          <div className="benefit">
            <span className="benefit-icon">⚡</span>
            <span>Fast access</span>
          </div>
          <div className="benefit">
            <span className="benefit-icon">📱</span>
            <span>Home screen</span>
          </div>
          <div className="benefit">
            <span className="benefit-icon">⚙️</span>
            <span>Full screen app</span>
          </div>
        </div>

        <div className="pwa-install-actions">
          <button className="btn btn-primary" onClick={handleInstall}>
            <span className="btn-icon">⬇️</span>
            Install
          </button>
          <button className="btn btn-secondary" onClick={handleLater}>
            Later
          </button>
        </div>

        <p className="pwa-install-hint">
          You can always install later from your browser menu.
        </p>
      </div>
    </BottomSheet>
  );
};
